import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface FeedbackData {
  overallRating: number;
  relevanceRating?: number;
  completenessRating?: number;
  clarityRating?: number;
  comment?: string;
  helpfulAspects?: string[];
  improvementSuggestions?: string[];
}

interface FeedbackFormProps {
  sessionId: string;
  onSubmit: (feedback: FeedbackData) => Promise<void>;
  onClose?: () => void;
  isSubmitting?: boolean;
}

export function FeedbackForm({
  sessionId: _sessionId,
  onSubmit,
  onClose,
  isSubmitting = false,
}: FeedbackFormProps) {
  // sessionId is passed for potential future use (logging, analytics)
  void _sessionId;
  const { t } = useTranslation();

  const helpfulAspectOptions = [
    { id: 'citations', label: t('feedbackForm.aspects.citations') },
    { id: 'structure', label: t('feedbackForm.aspects.structure') },
    { id: 'balance', label: t('feedbackForm.aspects.balance') },
    { id: 'recommendations', label: t('feedbackForm.aspects.recommendations') },
    { id: 'legal_bases', label: t('feedbackForm.aspects.legalBases') },
  ];

  const improvementOptions = [
    { id: 'more_recent', label: t('feedbackForm.improvements.moreRecent') },
    { id: 'more_precedents', label: t('feedbackForm.improvements.morePrecedents') },
    { id: 'more_detail', label: t('feedbackForm.improvements.moreDetail') },
    { id: 'simpler_language', label: t('feedbackForm.improvements.simplerLanguage') },
    { id: 'more_context', label: t('feedbackForm.improvements.moreContext') },
  ];
  const [overallRating, setOverallRating] = useState<number>(0);
  const [relevanceRating, setRelevanceRating] = useState<number>(0);
  const [completenessRating, setCompletenessRating] = useState<number>(0);
  const [clarityRating, setClarityRating] = useState<number>(0);
  const [comment, setComment] = useState('');
  const [helpfulAspects, setHelpfulAspects] = useState<string[]>([]);
  const [improvementSuggestions, setImprovementSuggestions] = useState<string[]>([]);
  const [showDetails, setShowDetails] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const toggleHelpfulAspect = (id: string) => {
    setHelpfulAspects((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
    );
  };

  const toggleImprovement = (id: string) => {
    setImprovementSuggestions((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const handleSubmit = async () => {
    if (overallRating === 0) return;

    const feedback: FeedbackData = {
      overallRating,
      ...(relevanceRating > 0 && { relevanceRating }),
      ...(completenessRating > 0 && { completenessRating }),
      ...(clarityRating > 0 && { clarityRating }),
      ...(comment.trim() && { comment: comment.trim() }),
      ...(helpfulAspects.length > 0 && { helpfulAspects }),
      ...(improvementSuggestions.length > 0 && { improvementSuggestions }),
    };

    await onSubmit(feedback);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800 p-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-full">
            <svg
              className="w-6 h-6 text-green-600 dark:text-green-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <div>
            <h3 className="font-medium text-green-800 dark:text-green-200">
              {t('feedbackForm.thankYou')}
            </h3>
            <p className="text-sm text-green-600 dark:text-green-400">
              {t('feedbackForm.thankYouMessage')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
            <svg
              className="w-5 h-5 text-blue-600 dark:text-blue-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
              />
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white">
              {t('feedbackForm.rateAnalysis')}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {t('feedbackForm.rateAnalysisDescription')}
            </p>
          </div>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Overall Rating */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          {t('feedbackForm.overallRating')}
        </label>
        <div className="flex items-center gap-2">
          {[1, 2, 3, 4, 5].map((rating) => (
            <button
              key={rating}
              type="button"
              onClick={() => setOverallRating(rating)}
              disabled={isSubmitting}
              className={`p-2 rounded-lg transition-all ${
                rating <= overallRating
                  ? 'text-yellow-500'
                  : 'text-slate-300 dark:text-slate-600 hover:text-slate-400'
              }`}
            >
              <svg
                className="w-8 h-8"
                fill={rating <= overallRating ? 'currentColor' : 'none'}
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                />
              </svg>
            </button>
          ))}
          <span className="ml-2 text-sm text-slate-500 dark:text-slate-400">
            {overallRating === 0 && t('feedbackForm.selectRating')}
            {overallRating === 1 && t('feedbackForm.veryPoor')}
            {overallRating === 2 && t('feedbackForm.poor')}
            {overallRating === 3 && t('feedbackForm.average')}
            {overallRating === 4 && t('feedbackForm.good')}
            {overallRating === 5 && t('feedbackForm.excellent')}
          </span>
        </div>
      </div>

      {/* Show detailed options toggle */}
      <button
        type="button"
        onClick={() => setShowDetails(!showDetails)}
        className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-4"
      >
        <svg
          className={`w-4 h-4 transition-transform ${showDetails ? 'rotate-90' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        {showDetails ? t('feedbackForm.hideDetails') : t('feedbackForm.addDetails')}
      </button>

      {showDetails && (
        <div className="space-y-6 border-t border-slate-200 dark:border-slate-700 pt-4">
          {/* Category Ratings */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Relevance */}
            <div>
              <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                {t('feedbackForm.relevance')}
              </label>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <button
                    key={rating}
                    type="button"
                    onClick={() => setRelevanceRating(rating)}
                    disabled={isSubmitting}
                    className={`p-1 ${
                      rating <= relevanceRating
                        ? 'text-yellow-500'
                        : 'text-slate-300 dark:text-slate-600'
                    }`}
                  >
                    <svg
                      className="w-5 h-5"
                      fill={rating <= relevanceRating ? 'currentColor' : 'none'}
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                      />
                    </svg>
                  </button>
                ))}
              </div>
            </div>

            {/* Completeness */}
            <div>
              <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                {t('feedbackForm.completeness')}
              </label>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <button
                    key={rating}
                    type="button"
                    onClick={() => setCompletenessRating(rating)}
                    disabled={isSubmitting}
                    className={`p-1 ${
                      rating <= completenessRating
                        ? 'text-yellow-500'
                        : 'text-slate-300 dark:text-slate-600'
                    }`}
                  >
                    <svg
                      className="w-5 h-5"
                      fill={rating <= completenessRating ? 'currentColor' : 'none'}
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                      />
                    </svg>
                  </button>
                ))}
              </div>
            </div>

            {/* Clarity */}
            <div>
              <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                {t('feedbackForm.clarity')}
              </label>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <button
                    key={rating}
                    type="button"
                    onClick={() => setClarityRating(rating)}
                    disabled={isSubmitting}
                    className={`p-1 ${
                      rating <= clarityRating
                        ? 'text-yellow-500'
                        : 'text-slate-300 dark:text-slate-600'
                    }`}
                  >
                    <svg
                      className="w-5 h-5"
                      fill={rating <= clarityRating ? 'currentColor' : 'none'}
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                      />
                    </svg>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Helpful Aspects */}
          <div>
            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
              {t('feedbackForm.mostHelpful')}
            </label>
            <div className="flex flex-wrap gap-2">
              {helpfulAspectOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => toggleHelpfulAspect(option.id)}
                  disabled={isSubmitting}
                  className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                    helpfulAspects.includes(option.id)
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-300 dark:border-green-700'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Improvement Suggestions */}
          <div>
            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
              {t('feedbackForm.improvementsLabel')}
            </label>
            <div className="flex flex-wrap gap-2">
              {improvementOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => toggleImprovement(option.id)}
                  disabled={isSubmitting}
                  className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                    improvementSuggestions.includes(option.id)
                      ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-700'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Comment */}
          <div>
            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
              {t('feedbackForm.additionalComments')}
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              disabled={isSubmitting}
              placeholder={t('feedbackForm.commentPlaceholder')}
              className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows={3}
            />
          </div>
        </div>
      )}

      {/* Submit Button */}
      <div className="mt-6 flex justify-end">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={overallRating === 0 || isSubmitting}
          className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
        >
          {isSubmitting ? (
            <>
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              {t('common.sending')}
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {t('feedbackForm.submit')}
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default FeedbackForm;
